import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import companyAccountsService from '../../../services/companyAccountsService';

const QuickActionsModal = ({ account, onClose, onActionComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBillingInput, setShowBillingInput] = useState(false);
  const [newBillingAmount, setNewBillingAmount] = useState(account?.monthlyPriceKina || 0);

  const handleSuspend = async () => {
    if (!window.confirm(`Are you sure you want to suspend ${account?.companyName}? This will disable their access.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await companyAccountsService?.suspendAccount(account?.id);
      onActionComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to suspend account');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!window.confirm(`Are you sure you want to reactivate ${account?.companyName}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await companyAccountsService?.reactivateAccount(account?.id);
      onActionComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to reactivate account');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBilling = async () => {
    if (newBillingAmount <= 0) {
      setError('Billing amount must be greater than 0');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await companyAccountsService?.updateBillingAmount(account?.id, newBillingAmount);
      setShowBillingInput(false);
      onActionComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to update billing amount');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectAccess = () => {
    alert(`Direct account access functionality will navigate to ${account?.companyName} dashboard`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">{account?.companyName}</h2>
              <p className="text-sm text-muted-foreground">{account?.companyLegalName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Account Overview */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-foreground mb-3">Account Overview</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-sm font-medium text-foreground">
                  {account?.subscriptionStatus === 'active' ? 'Active' : 'Suspended'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="text-sm font-medium text-foreground">{account?.employeeCount}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Compliance Score</p>
                <p className="text-sm font-medium text-foreground">{account?.complianceScore}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Billing</p>
                <p className="text-sm font-medium text-foreground">K{account?.monthlyPriceKina?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Subscription Tier</p>
                <p className="text-sm font-medium text-foreground">{account?.subscriptionTier}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contact Email</p>
                <p className="text-sm font-medium text-foreground">{account?.contactEmail}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-foreground mb-3">Quick Actions</h3>
            <div className="space-y-3">
              {/* Suspend/Reactivate */}
              {account?.subscriptionStatus === 'active' ? (
                <Button
                  variant="outline"
                  className="w-full justify-start text-error border-error hover:bg-error/10"
                  onClick={handleSuspend}
                  disabled={loading}
                >
                  <Icon name="Ban" size={16} className="mr-2" />
                  Suspend Account
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start text-success border-success hover:bg-success/10"
                  onClick={handleReactivate}
                  disabled={loading}
                >
                  <Icon name="CheckCircle" size={16} className="mr-2" />
                  Reactivate Account
                </Button>
              )}

              {/* Billing Adjustment */}
              <div>
                {!showBillingInput ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowBillingInput(true)}
                  >
                    <Icon name="DollarSign" size={16} className="mr-2" />
                    Adjust Billing Amount
                  </Button>
                ) : (
                  <div className="border border-border rounded-md p-3">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      New Monthly Amount (PNG Kina)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={newBillingAmount}
                        onChange={(e) => setNewBillingAmount(parseFloat(e?.target?.value) || 0)}
                        className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        min="0"
                        step="0.01"
                      />
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleUpdateBilling}
                        disabled={loading}
                      >
                        Update
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBillingInput(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Direct Access */}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleDirectAccess}
              >
                <Icon name="ExternalLink" size={16} className="mr-2" />
                Access Company Dashboard
              </Button>
            </div>
          </div>

          {/* Health Indicators */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-3">Health Indicators</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <Icon name="Shield" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Compliance Status</span>
                </div>
                <span className={`text-sm font-medium ${
                  account?.complianceScore >= 90 ? 'text-success' :
                  account?.complianceScore >= 70 ? 'text-warning' : 'text-error'
                }`}>
                  {account?.complianceScore >= 90 ? 'Excellent' :
                   account?.complianceScore >= 70 ? 'Good' : 'Needs Attention'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <Icon name="AlertTriangle" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Critical Violations</span>
                </div>
                <span className={`text-sm font-medium ${
                  account?.criticalViolations === 0 ? 'text-success' : 'text-error'
                }`}>
                  {account?.criticalViolations || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  <Icon name="CreditCard" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">Billing Status</span>
                </div>
                <span className="text-sm font-medium text-success">
                  Current
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickActionsModal;